import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { AdminLayout } from '../components/AdminLayout';
import { toast } from 'sonner';
import { 
  Palette, Plus, Edit2, Trash2, X, Save, RefreshCw, Shield, LogOut,
  Calendar, Clock, DollarSign, Users, MapPin, Upload, Image, User, UserPlus, List,
  Bold, Italic, Underline, Link as LinkIcon, Download, CloudUpload, Database, AlertTriangle, Ban,
  CalendarClock, UserX, FileText, Mail, Archive, ArchiveRestore, Copy, AlertCircle, Share2
} from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import LinkExtension from '@tiptap/extension-link';
import { EmailParticipantsModal } from '../components/EmailParticipantsModal';
import { ShareRosterModal } from '../components/ShareRosterModal';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';

const API_URL = "";

// Rich Text Editor Component
const RichTextEditor = ({ value, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      LinkExtension.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-cafa-blue underline',
        },
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'focus:outline-none',
      },
      handleDOMEvents: {
        focus: (view, event) => {
          // Prevent scroll on focus
          event.preventDefault();
          return false;
        },
      },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL:', previousUrl || 'https://');
    
    if (url === null) return;
    
    if (url === '') {
      editor.chain().focus({ scrollIntoView: false }).extendMarkRange('link').unsetLink().run();
      return;
    }
    
    editor.chain().focus({ scrollIntoView: false }).extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="border border-stone-300 rounded-md overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 bg-stone-100 border-b border-stone-300">
        <button
          type="button"
          onClick={() => editor.chain().focus({ scrollIntoView: false }).toggleBold().run()}
          className={`p-2 rounded hover:bg-stone-200 ${editor.isActive('bold') ? 'bg-stone-300' : ''}`}
          title="Bold"
        >
          <Bold size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus({ scrollIntoView: false }).toggleItalic().run()}
          className={`p-2 rounded hover:bg-stone-200 ${editor.isActive('italic') ? 'bg-stone-300' : ''}`}
          title="Italic"
        >
          <Italic size={16} />
        </button>
        <div className="w-px h-6 bg-stone-300 mx-1" />
        <button
          type="button"
          onClick={setLink}
          className={`p-2 rounded hover:bg-stone-200 ${editor.isActive('link') ? 'bg-stone-300' : ''}`}
          title="Add Link"
        >
          <LinkIcon size={16} />
        </button>
        {editor.isActive('link') && (
          <button
            type="button"
            onClick={() => editor.chain().focus({ scrollIntoView: false }).unsetLink().run()}
            className="p-2 rounded hover:bg-stone-200 text-red-600"
            title="Remove Link"
          >
            <X size={16} />
          </button>
        )}
      </div>
      {/* Editor */}
      <EditorContent 
        editor={editor} 
        className="prose prose-stone prose-sm max-w-none p-3 min-h-[200px] focus:outline-none [&_.ProseMirror]:min-h-[180px] [&_.ProseMirror]:outline-none"
      />
    </div>
  );
};

export const AdminWorkshops = () => {
  const { user, token, logout, isAuthenticated, loading: authLoading } = useAuth();
  const [workshops, setWorkshops] = useState([]);
  const [workshopSettings, setWorkshopSettings] = useState({});
  const [enrollmentCounts, setEnrollmentCounts] = useState({});
  const [enrollmentDetails, setEnrollmentDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingWorkshop, setEditingWorkshop] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddRegistrationModal, setShowAddRegistrationModal] = useState(null);
  const [showAttendeesModal, setShowAttendeesModal] = useState(null);
  const [shareRoster, setShareRoster] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncData, setSyncData] = useState(null);
  const [productionUrl, setProductionUrl] = useState('');
  const [productionToken, setProductionToken] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(null);
  const [cancelPreview, setCancelPreview] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancelResult, setCancelResult] = useState(null);
  // Reschedule state
  const [showRescheduleModal, setShowRescheduleModal] = useState(null);
  const [emailWorkshop, setEmailWorkshop] = useState(null);
  // Workshop list tab + archive/duplicate state (P2 admin tabs)
  const [workshopTab, setWorkshopTab] = useState('upcoming'); // 'upcoming' | 'past' | 'cancelled' | 'archived'
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [unarchiveTarget, setUnarchiveTarget] = useState(null);
  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [reschedulePreview, setReschedulePreview] = useState(null);
  const [rescheduleData, setRescheduleData] = useState({ newStartDate: '', newEndDate: '', newSchedule: '', message: '' });
  const [rescheduling, setRescheduling] = useState(false);
  const [rescheduleResult, setRescheduleResult] = useState(null);
  // Individual Cancellation state
  const [showIndividualCancelModal, setShowIndividualCancelModal] = useState(null);
  const [individualCancelEligibility, setIndividualCancelEligibility] = useState(null);
  const [individualCancelReason, setIndividualCancelReason] = useState('');
  const [individualCancelMethod, setIndividualCancelMethod] = useState('email');
  const [processingIndividualCancel, setProcessingIndividualCancel] = useState(false);
  // Pending Requests
  const [pendingCancellations, setPendingCancellations] = useState([]);
  const [showPendingRequestsModal, setShowPendingRequestsModal] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const isAdmin = user?.access && ['Administrator', 'Webmaster', 'Admin'].includes(user.access);

  const emptyWorkshop = {
    title: '',
    instructor: '',
    date: '',
    endDate: '',
    schedule: '',
    time: '',
    description: '',
    level: 'All Levels',
    price: 0,
    memberPrice: 0,
    nonMemberPrice: 0,
    materials: '',
    location: '115 Melrose Place, San Antonio, TX',
    instructorImage: '',
    images: [],
    curriculum: [],
    materials_detailed: [],
    maxStudents: 12
  };

  const emptyRegistration = {
    payer_name: '',
    payer_email: '',
    payer_phone: '',
    amount_paid: 0,
    payment_method: 'cash',
    notes: ''
  };

  const [formData, setFormData] = useState(emptyWorkshop);
  const [registrationForm, setRegistrationForm] = useState(emptyRegistration);

  useEffect(() => {
    if (token && isAdmin) {
      fetchData();
    }
  }, [token, isAdmin]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [workshopsRes, settingsRes, enrollmentsRes] = await Promise.all([
        // Use admin endpoint to get ALL workshops including cancelled
        fetch(`${API_URL}/api/content/admin/workshops`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/workshops/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/content/enrollments`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (workshopsRes.ok) {
        setWorkshops(await workshopsRes.json());
      }
      
      if (settingsRes.ok) {
        const settings = await settingsRes.json();
        const settingsMap = {};
        settings.forEach(s => { settingsMap[s.workshop_id] = s; });
        setWorkshopSettings(settingsMap);
      }

      if (enrollmentsRes.ok) {
        const enrollments = await enrollmentsRes.json();
        const counts = {};
        const details = {};
        enrollments.forEach(e => {
          if (e.id.startsWith('workshop_')) {
            const id = e.id.replace('workshop_', '');
            counts[id] = e.count;
            details[id] = e.attendees || [];
          }
        });
        setEnrollmentCounts(counts);
        setEnrollmentDetails(details);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load workshops');
    } finally {
      setLoading(false);
    }
  };

  const getMaxStudents = (workshopId) => {
    return workshopSettings[workshopId]?.max_slots || 12;
  };

  const getEnrolled = (workshopId) => {
    return enrollmentCounts[workshopId] || 0;
  };

  const getAttendees = (workshopId) => {
    return enrollmentDetails[workshopId] || [];
  };

  const handleEdit = (workshop) => {
    const maxStudents = getMaxStudents(workshop.id);
    setFormData({
      ...workshop,
      images: workshop.images || [],
      curriculum: workshop.curriculum || [],
      materials_detailed: workshop.materials_detailed || [],
      maxStudents
    });
    setEditingWorkshop(workshop);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // For now, we'll use a URL input since we don't have a general file upload endpoint
    // In production, you'd upload to a storage service
    toast.info('For instructor images, please provide a URL. File upload coming soon.');
  };

  const handleSave = async () => {
    if (!formData.title || !formData.instructor || !formData.date) {
      toast.error('Please fill in required fields: Title, Instructor, and Date');
      return;
    }

    setSaving(true);
    try {
      // Save workshop data
      const workshopData = { ...formData };
      delete workshopData.maxStudents; // Remove maxStudents from workshop data
      delete workshopData.id; // Remove id for update

      const response = await fetch(`${API_URL}/api/content/workshops/${editingWorkshop.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(workshopData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update workshop');
      }

      // Save workshop settings (max students) - non-blocking
      try {
        await fetch(`${API_URL}/api/workshops/settings/${editingWorkshop.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            max_slots: formData.maxStudents,
            notify_at_slots_remaining: Math.min(3, formData.maxStudents)
          })
        });
      } catch (settingsError) {
        console.warn('Workshop settings update failed:', settingsError);
        // Don't fail the entire operation for settings error
      }

      toast.success('Workshop updated successfully');
      setEditingWorkshop(null);
      setFormData(emptyWorkshop);
      fetchData();
    } catch (error) {
      console.error('Error saving workshop:', error);
      toast.error(error.message || 'Failed to save workshop');
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async () => {
    if (!formData.title || !formData.instructor || !formData.date) {
      toast.error('Please fill in required fields: Title, Instructor, and Date');
      return;
    }

    setSaving(true);
    try {
      const workshopData = { ...formData };
      const maxStudents = workshopData.maxStudents;
      delete workshopData.maxStudents;

      const response = await fetch(`${API_URL}/api/content/workshops`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(workshopData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to create workshop');
      }

      const result = await response.json();
      const newWorkshopId = result.id;

      // Create workshop settings for max students (non-blocking)
      try {
        const settingsResponse = await fetch(`${API_URL}/api/workshops/settings`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            workshop_id: String(newWorkshopId),
            workshop_name: formData.title,
            max_slots: maxStudents,
            notify_at_slots_remaining: Math.min(3, maxStudents)
          })
        });
        
        if (!settingsResponse.ok) {
          console.warn('Workshop settings creation failed, but workshop was created');
        }
      } catch (settingsError) {
        console.warn('Workshop settings creation error:', settingsError);
        // Don't fail the entire operation for settings error
      }

      toast.success('Workshop created successfully');
      setShowAddModal(false);
      setFormData(emptyWorkshop);
      fetchData();
    } catch (error) {
      console.error('Error creating workshop:', error);
      toast.error(error.message || 'Failed to create workshop');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (workshop) => {
    if (!confirm(`Delete "${workshop.title}"? This cannot be undone.`)) return;

    try {
      const response = await fetch(`${API_URL}/api/content/workshops/${workshop.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Workshop deleted');
        fetchData();
      } else {
        toast.error('Failed to delete workshop');
      }
    } catch (error) {
      toast.error('Error deleting workshop');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // ============== ARCHIVE / UNARCHIVE / DUPLICATE (P2 admin tabs) ==============

  const performArchive = async (workshop) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/content/workshops/${workshop.id}/archive`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success(`"${workshop.title}" archived. Roster and history preserved.`);
        setArchiveTarget(null);
        fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to archive workshop');
      }
    } catch (e) {
      toast.error('Error archiving workshop');
    } finally {
      setActionLoading(false);
    }
  };

  const performUnarchive = async (workshop) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/content/workshops/${workshop.id}/unarchive`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success(`"${workshop.title}" unarchived.`);
        setUnarchiveTarget(null);
        fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to unarchive workshop');
      }
    } catch (e) {
      toast.error('Error unarchiving workshop');
    } finally {
      setActionLoading(false);
    }
  };

  const performDuplicate = async (workshop) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/content/workshops/${workshop.id}/duplicate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Draft created (ID #${data.id}). Set start/end dates before publishing.`);
        setDuplicateTarget(null);
        // Switch to upcoming tab so admin can find the new draft (drafts bucket here)
        setWorkshopTab('upcoming');
        fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to duplicate workshop');
      }
    } catch (e) {
      toast.error('Error duplicating workshop');
    } finally {
      setActionLoading(false);
    }
  };

  // ============== WORKSHOP CANCELLATION FUNCTIONS ==============

  const openCancelModal = async (workshop) => {
    setShowCancelModal(workshop);
    setCancelPreview(null);
    setCancelReason('');
    setCancelResult(null);
    
    // Fetch cancellation preview
    try {
      const response = await fetch(`${API_URL}/api/admin/workshops/${workshop.id}/cancellation-preview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const preview = await response.json();
        setCancelPreview(preview);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to load cancellation preview');
        setShowCancelModal(null);
      }
    } catch (error) {
      console.error('Error fetching cancellation preview:', error);
      toast.error('Failed to load cancellation preview');
      setShowCancelModal(null);
    }
  };

  const handleCancelWorkshop = async () => {
    if (!showCancelModal || !cancelPreview) return;
    
    setCancelling(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/workshops/${showCancelModal.id}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cancellation_reason: cancelReason || null
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        setCancelResult(result);
        toast.success('Workshop cancelled successfully');
        fetchData(); // Refresh workshop list
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to cancel workshop');
      }
    } catch (error) {
      console.error('Error cancelling workshop:', error);
      toast.error('Failed to cancel workshop');
    } finally {
      setCancelling(false);
    }
  };

  const closeCancelModal = () => {
    setShowCancelModal(null);
    setCancelPreview(null);
    setCancelReason('');
    setCancelResult(null);
  };

  // ============== RESCHEDULE FUNCTIONS ==============

  const openRescheduleModal = async (workshop) => {
    setShowRescheduleModal(workshop);
    setReschedulePreview(null);
    setRescheduleData({ newStartDate: '', newEndDate: '', newSchedule: workshop.schedule || '', message: '' });
    setRescheduleResult(null);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/workshops/${workshop.id}/reschedule-preview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const preview = await response.json();
        setReschedulePreview(preview);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to load reschedule preview');
        setShowRescheduleModal(null);
      }
    } catch (error) {
      console.error('Error fetching reschedule preview:', error);
      toast.error('Failed to load reschedule preview');
      setShowRescheduleModal(null);
    }
  };

  const handleRescheduleWorkshop = async () => {
    if (!showRescheduleModal || !rescheduleData.newStartDate) {
      toast.error('Please enter a new start date');
      return;
    }
    
    setRescheduling(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/workshops/${showRescheduleModal.id}/reschedule`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          new_start_date: rescheduleData.newStartDate,
          new_end_date: rescheduleData.newEndDate || null,
          new_schedule: rescheduleData.newSchedule || null,
          message_to_registrants: rescheduleData.message || null
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        setRescheduleResult(result);
        toast.success('Workshop rescheduled successfully');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reschedule workshop');
      }
    } catch (error) {
      console.error('Error rescheduling workshop:', error);
      toast.error('Failed to reschedule workshop');
    } finally {
      setRescheduling(false);
    }
  };

  const closeRescheduleModal = () => {
    setShowRescheduleModal(null);
    setReschedulePreview(null);
    setRescheduleData({ newStartDate: '', newEndDate: '', newSchedule: '', message: '' });
    setRescheduleResult(null);
  };

  // ============== INDIVIDUAL CANCELLATION FUNCTIONS ==============

  const openIndividualCancelModal = async (workshop, registration) => {
    setShowIndividualCancelModal({ workshop, registration });
    setIndividualCancelEligibility(null);
    setIndividualCancelReason('');
    setIndividualCancelMethod('email');
    
    try {
      const response = await fetch(
        `${API_URL}/api/admin/workshops/${workshop.id}/registration/${registration.registration_id}/cancellation-eligibility`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const eligibility = await response.json();
        setIndividualCancelEligibility(eligibility);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to check eligibility');
        setShowIndividualCancelModal(null);
      }
    } catch (error) {
      console.error('Error checking eligibility:', error);
      toast.error('Failed to check eligibility');
      setShowIndividualCancelModal(null);
    }
  };

  const handleSubmitIndividualCancellation = async () => {
    if (!showIndividualCancelModal || !individualCancelReason) {
      toast.error('Please provide a cancellation reason');
      return;
    }
    
    const { workshop, registration } = showIndividualCancelModal;
    setProcessingIndividualCancel(true);
    
    try {
      // Submit cancellation request
      const submitResponse = await fetch(
        `${API_URL}/api/admin/workshops/${workshop.id}/registration/${registration.registration_id}/request-cancellation`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            registration_id: registration.registration_id,
            request_method: individualCancelMethod,
            cancellation_reason: individualCancelReason
          })
        }
      );
      
      if (!submitResponse.ok) {
        const error = await submitResponse.json();
        throw new Error(error.detail || 'Failed to submit cancellation');
      }
      
      // Auto-approve for immediate processing
      const approveResponse = await fetch(
        `${API_URL}/api/admin/workshops/${workshop.id}/registration/${registration.registration_id}/approve-cancellation`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            registration_id: registration.registration_id,
            override_policy: false
          })
        }
      );
      
      if (approveResponse.ok) {
        const result = await approveResponse.json();
        toast.success(`Cancellation processed. Refund: $${result.refund_amount.toFixed(2)}`);
        setShowIndividualCancelModal(null);
        fetchData();
      } else {
        const error = await approveResponse.json();
        throw new Error(error.detail || 'Failed to process cancellation');
      }
    } catch (error) {
      console.error('Error processing cancellation:', error);
      toast.error(error.message);
    } finally {
      setProcessingIndividualCancel(false);
    }
  };

  const fetchPendingCancellations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/workshops/pending-cancellation-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPendingCancellations(data.pending_requests || []);
      }
    } catch (error) {
      console.error('Error fetching pending cancellations:', error);
    }
  };

  // ============== DATA SYNC FUNCTIONS ==============
  
  const handleExportData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/content/export/workshops`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSyncData(data);
        setShowSyncModal(true);
        toast.success(`Exported ${data.count} workshops`);
      } else {
        toast.error('Failed to export data');
      }
    } catch (error) {
      toast.error('Error exporting data');
    }
  };

  const handleSyncToProduction = async () => {
    if (!productionUrl || !productionToken) {
      toast.error('Please enter production URL and token');
      return;
    }
    
    if (!syncData?.workshops) {
      toast.error('No data to sync. Export first.');
      return;
    }

    setSyncing(true);
    try {
      // Clean the URL
      let url = productionUrl.trim();
      if (url.endsWith('/')) url = url.slice(0, -1);
      
      const response = await fetch(`${url}/api/content/import/workshops`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${productionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          workshops: syncData.workshops,
          mode: 'replace'
        })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Synced! Added: ${result.added}, Updated: ${result.updated}`);
        setShowSyncModal(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to sync to production');
      }
    } catch (error) {
      toast.error('Error syncing: ' + error.message);
    } finally {
      setSyncing(false);
    }
  };

  const copyExportData = () => {
    if (syncData) {
      navigator.clipboard.writeText(JSON.stringify(syncData.workshops, null, 2));
      toast.success('Workshop data copied to clipboard');
    }
  };

  const openAddModal = () => {
    setFormData(emptyWorkshop);
    setShowAddModal(true);
  };

  const openAddRegistrationModal = (workshop) => {
    setRegistrationForm({
      ...emptyRegistration,
      amount_paid: workshop.memberPrice || workshop.price || 0
    });
    setShowAddRegistrationModal(workshop);
  };

  const handleAddRegistration = async () => {
    if (!registrationForm.payer_name || !registrationForm.payer_email) {
      toast.error('Please enter name and email');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/registrations/manual`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          registration_type: 'workshop',
          workshop_id: String(showAddRegistrationModal.id),
          workshop_name: showAddRegistrationModal.title,
          payer_name: registrationForm.payer_name,
          payer_email: registrationForm.payer_email,
          payer_phone: registrationForm.payer_phone,
          amount_paid: registrationForm.amount_paid,
          payment_method: registrationForm.payment_method,
          notes: registrationForm.notes
        })
      });

      if (response.ok) {
        toast.success('Registration added successfully');
        setShowAddRegistrationModal(null);
        setRegistrationForm(emptyRegistration);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add registration');
      }
    } catch (error) {
      console.error('Error adding registration:', error);
      toast.error('Failed to add registration');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cafa-red"></div>
      </div>
    );
  }

  const workshopFormContent = (isEdit = false) => (
    <div className="space-y-6">
      {/* Basic Info */}
      <div className="border-b border-stone-200 pb-4">
        <h3 className="font-semibold text-stone-800 mb-3">Basic Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label>Title <span className="text-red-500">*</span></Label>
            <Input 
              value={formData.title} 
              onChange={(e) => setFormData({...formData, title: e.target.value})} 
              className="mt-1"
              placeholder="e.g., Portrait Painting Master Class"
            />
          </div>
          <div>
            <Label>Instructor <span className="text-red-500">*</span></Label>
            <Input 
              value={formData.instructor} 
              onChange={(e) => setFormData({...formData, instructor: e.target.value})} 
              className="mt-1"
              placeholder="e.g., John Smith"
            />
          </div>
          <div>
            <Label>Level</Label>
            <select 
              value={formData.level} 
              onChange={(e) => setFormData({...formData, level: e.target.value})} 
              className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-md"
            >
              <option value="All Levels">All Levels</option>
              <option value="Beginner">Beginner</option>
              <option value="Intermediate">Intermediate</option>
              <option value="Advanced">Advanced</option>
              <option value="Intermediate to Advanced">Intermediate to Advanced</option>
            </select>
          </div>
        </div>
      </div>

      {/* Description - Rich Text Editor */}
      <div className="border-b border-stone-200 pb-4">
        <h3 className="font-semibold text-stone-800 mb-3">Description</h3>
        <p className="text-sm text-stone-500 mb-2">Use the toolbar to format text: <strong>Bold</strong>, <em>Italic</em>, or add links.</p>
        <RichTextEditor
          value={formData.description}
          onChange={(content) => setFormData({...formData, description: content})}
        />
        <p className="text-xs text-stone-500 mt-2">
          Rich text supported: Bold, Italic, Links
        </p>
      </div>

      {/* Schedule */}
      <div className="border-b border-stone-200 pb-4">
        <h3 className="font-semibold text-stone-800 mb-3 flex items-center gap-2">
          <Calendar size={18} /> Schedule
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Start Date <span className="text-red-500">*</span></Label>
            <Input 
              type="date"
              value={formData.date} 
              onChange={(e) => setFormData({...formData, date: e.target.value})} 
              className="mt-1"
              
            />
          </div>
          <div>
            <Label>End Date</Label>
            <Input 
              type="date"
              value={formData.endDate} 
              onChange={(e) => setFormData({...formData, endDate: e.target.value})} 
              className="mt-1"
              
            />
          </div>
          <div>
            <Label>Schedule Text</Label>
            <Input 
              value={formData.schedule} 
              onChange={(e) => setFormData({...formData, schedule: e.target.value})} 
              className="mt-1"
              placeholder="e.g., Friday-Sunday, March 15-17, 2026"
              
            />
          </div>
          <div>
            <Label>Time</Label>
            <Input 
              value={formData.time} 
              onChange={(e) => setFormData({...formData, time: e.target.value})} 
              className="mt-1"
              placeholder="e.g., 9:00 AM - 4:00 PM"
              
            />
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div className="border-b border-stone-200 pb-4">
        <h3 className="font-semibold text-stone-800 mb-3 flex items-center gap-2">
          <DollarSign size={18} /> Pricing
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Base Price ($)</Label>
            <Input 
              type="number"
              value={formData.price} 
              onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value) || 0})} 
              className="mt-1"
              
            />
          </div>
          <div>
            <Label>Member Price ($)</Label>
            <Input 
              type="number"
              value={formData.memberPrice} 
              onChange={(e) => setFormData({...formData, memberPrice: parseFloat(e.target.value) || 0})} 
              className="mt-1"
              
            />
          </div>
          <div>
            <Label>Non-Member Price ($)</Label>
            <Input 
              type="number"
              value={formData.nonMemberPrice} 
              onChange={(e) => setFormData({...formData, nonMemberPrice: parseFloat(e.target.value) || 0})} 
              className="mt-1"
              
            />
          </div>
        </div>
      </div>

      {/* Capacity */}
      <div className="border-b border-stone-200 pb-4">
        <h3 className="font-semibold text-stone-800 mb-3 flex items-center gap-2">
          <Users size={18} /> Student Capacity
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Maximum Students</Label>
            <Input 
              type="number"
              min="1"
              max="100"
              value={formData.maxStudents} 
              onChange={(e) => setFormData({...formData, maxStudents: parseInt(e.target.value) || 12})} 
              className="mt-1"
              
            />
            <p className="text-xs text-stone-500 mt-1">Registration will close when this limit is reached</p>
          </div>
          {isEdit && (
            <div className="flex items-center">
              <div className="bg-stone-100 rounded-lg px-4 py-3">
                <p className="text-sm text-stone-600">Currently Enrolled</p>
                <p className="text-2xl font-bold text-cafa-red">{getEnrolled(editingWorkshop?.id)}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Location & Materials */}
      <div className="border-b border-stone-200 pb-4">
        <h3 className="font-semibold text-stone-800 mb-3 flex items-center gap-2">
          <MapPin size={18} /> Location & Materials
        </h3>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <Label>Location</Label>
            <Input 
              value={formData.location} 
              onChange={(e) => setFormData({...formData, location: e.target.value})} 
              className="mt-1"
              
            />
          </div>
          <div>
            <Label>Materials Required</Label>
            <Textarea 
              value={formData.materials} 
              onChange={(e) => setFormData({...formData, materials: e.target.value})} 
              className="mt-1"
              rows={2}
              placeholder="List materials students should bring..."
              
            />
          </div>
        </div>
      </div>

      {/* Faculty/Instructor Photo */}
      <div className="border-b border-stone-200 pb-4">
        <h3 className="font-semibold text-stone-800 mb-3 flex items-center gap-2">
          <User size={18} /> Faculty Photo
        </h3>
        <p className="text-sm text-stone-500 mb-3">Upload a photo of the instructor. This appears in the workshop listing alongside the details.</p>
        
        <div className="flex items-start gap-4">
          {/* Current instructor image preview */}
          {formData.instructorImage && (
            <div className="relative">
              <img 
                src={formData.instructorImage.startsWith('/api') ? `${API_URL}${formData.instructorImage}` : formData.instructorImage} 
                alt="Instructor"
                className="w-32 h-32 rounded-lg object-cover border border-stone-200"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <button
                type="button"
                onClick={() => setFormData({...formData, instructorImage: ''})}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
              >
                <X size={14} />
              </button>
            </div>
          )}
          
          {/* Upload or URL input */}
          <div className="flex-1 space-y-3">
            <div className="flex gap-2">
              <Input 
                value={formData.instructorImage || ''} 
                onChange={(e) => setFormData({...formData, instructorImage: e.target.value})} 
                placeholder="Enter instructor photo URL"
                className="flex-1"
              />
            </div>
            <p className="text-xs text-stone-500">Enter a URL for the instructor&apos;s photo (e.g., from Cloudinary or another image host)</p>
          </div>
        </div>
      </div>

      {/* Workshop Images (Slider) */}
      <div className="border-b border-stone-200 pb-4">
        <h3 className="font-semibold text-stone-800 mb-3 flex items-center gap-2">
          <Image size={18} /> Workshop Images (Primary)
        </h3>
        <p className="text-sm text-stone-500 mb-3">Add up to 3 images for the main workshop display/slider. These are the primary images shown on the workshop page.</p>
        
        {/* Image previews */}
        <div className="flex gap-4 mb-4 flex-wrap">
          {(formData.images || []).map((img, index) => (
            <div key={index} className="relative">
              <img 
                src={img.startsWith('/api') ? `${API_URL}${img}` : img} 
                alt={`Workshop image ${index + 1}`}
                className="w-24 h-24 rounded-lg object-cover border border-stone-200"
                onError={(e) => { e.target.src = 'https://via.placeholder.com/96?text=Error'; }}
              />
              <button
                type="button"
                onClick={() => {
                  const newImages = [...(formData.images || [])];
                  newImages.splice(index, 1);
                  setFormData({...formData, images: newImages});
                }}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
              >
                <X size={14} />
              </button>
            </div>
          ))}
          {(!formData.images || formData.images.length < 3) && (
            <div 
              className="w-24 h-24 rounded-lg bg-stone-100 flex items-center justify-center border border-dashed border-stone-300 cursor-pointer hover:bg-stone-200 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? (
                <RefreshCw size={24} className="text-stone-400 animate-spin" />
              ) : (
                <Upload size={24} className="text-stone-400" />
              )}
            </div>
          )}
        </div>
        
        {/* File upload input (hidden) */}
        <input
          type="file"
          ref={fileInputRef}
          accept="image/jpeg,image/png,image/gif,image/webp"
          className="hidden"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            
            if (formData.images && formData.images.length >= 3) {
              toast.error('Maximum 3 images allowed');
              return;
            }
            
            setUploading(true);
            const formDataUpload = new FormData();
            formDataUpload.append('file', file);
            
            try {
              const res = await fetch(`${API_URL}/api/uploads/image`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formDataUpload
              });
              
              if (res.ok) {
                const data = await res.json();
                setFormData({
                  ...formData,
                  images: [...(formData.images || []), data.url]
                });
                toast.success('Image uploaded successfully');
              } else {
                const error = await res.json();
                toast.error(error.detail || 'Failed to upload image');
              }
            } catch (err) {
              console.error('Upload error:', err);
              toast.error('Failed to upload image');
            } finally {
              setUploading(false);
              e.target.value = ''; // Reset file input
            }
          }}
        />
        
        {/* Upload button and URL input */}
        {(!formData.images || formData.images.length < 3) && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <><RefreshCw size={16} className="mr-2 animate-spin" /> Uploading...</>
                ) : (
                  <><Upload size={16} className="mr-2" /> Upload Image</>
                )}
              </Button>
              <span className="text-stone-400 self-center">or</span>
            </div>
            <div className="flex gap-2">
              <Input 
                id="newImageUrl"
                placeholder="Enter image URL"
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  const input = document.getElementById('newImageUrl');
                  if (input.value) {
                    setFormData({
                      ...formData, 
                      images: [...(formData.images || []), input.value]
                    });
                    input.value = '';
                  }
                }}
              >
                <Plus size={16} className="mr-1" /> Add URL
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* What You'll Learn (Curriculum) */}
      <div className="border-b border-stone-200 pb-4">
        <h3 className="font-semibold text-stone-800 mb-3">What Students Will Learn</h3>
        <p className="text-sm text-stone-500 mb-3">Add bullet points describing what the workshop covers. This will appear in an expandable section.</p>
        
        <div className="space-y-2 mb-3">
          {(formData.curriculum || []).map((item, index) => (
            <div key={index} className="flex gap-2 items-center">
              <span className="text-cafa-red">•</span>
              <Input 
                value={item}
                onChange={(e) => {
                  const newCurriculum = [...(formData.curriculum || [])];
                  newCurriculum[index] = e.target.value;
                  setFormData({...formData, curriculum: newCurriculum});
                }}
                className="flex-1"
              />
              <button
                type="button"
                onClick={() => {
                  const newCurriculum = [...(formData.curriculum || [])];
                  newCurriculum.splice(index, 1);
                  setFormData({...formData, curriculum: newCurriculum});
                }}
                className="text-red-500 hover:text-red-600 p-1"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
        
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setFormData({...formData, curriculum: [...(formData.curriculum || []), '']})}
        >
          <Plus size={14} className="mr-1" /> Add Learning Point
        </Button>
      </div>

      {/* Materials Needed (Detailed) */}
      <div>
        <h3 className="font-semibold text-stone-800 mb-3">Detailed Materials List</h3>
        <p className="text-sm text-stone-500 mb-3">Add a detailed list of materials students need. This will appear in an expandable section.</p>
        
        <div className="space-y-2 mb-3">
          {(formData.materials_detailed || []).map((item, index) => (
            <div key={index} className="flex gap-2 items-center">
              <span className="text-cafa-red">•</span>
              <Input 
                value={item}
                onChange={(e) => {
                  const newMaterials = [...(formData.materials_detailed || [])];
                  newMaterials[index] = e.target.value;
                  setFormData({...formData, materials_detailed: newMaterials});
                }}
                className="flex-1"
              />
              <button
                type="button"
                onClick={() => {
                  const newMaterials = [...(formData.materials_detailed || [])];
                  newMaterials.splice(index, 1);
                  setFormData({...formData, materials_detailed: newMaterials});
                }}
                className="text-red-500 hover:text-red-600 p-1"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
        
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setFormData({...formData, materials_detailed: [...(formData.materials_detailed || []), '']})}
        >
          <Plus size={14} className="mr-1" /> Add Material
        </Button>
      </div>
    </div>
  );

  return (
    <AdminLayout title="Workshop Management" subtitle="Create, edit, and manage workshops">
      <div data-testid="admin-workshops-page">
      {/* Actions Bar */}
      <section className="py-4 border-b border-stone-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="text-center px-4 py-2 bg-stone-100 rounded-lg">
                <p className="text-2xl font-bold text-stone-800">{workshops.length}</p>
                <p className="text-xs text-stone-500">Workshops</p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={fetchData} size="sm">
                <RefreshCw size={16} className="mr-1" /> Refresh
              </Button>
              <Button variant="outline" onClick={handleExportData} size="sm" className="border-cafa-blue text-cafa-blue hover:bg-cafa-blue-50">
                <Database size={16} className="mr-1" /> Sync Data
              </Button>
              <Button onClick={openAddModal} size="sm" className="bg-cafa-red hover:bg-cafa-red-900 text-white">
                <Plus size={16} className="mr-1" /> Add Workshop
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Workshop List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cafa-red"></div>
          </div>
        ) : workshops.length === 0 ? (
          <Card className="border-stone-200">
            <CardContent className="py-12 text-center">
              <Palette size={48} className="mx-auto mb-4 text-stone-300" />
              <h3 className="text-lg font-medium text-stone-900 mb-2">No Workshops Yet</h3>
              <p className="text-stone-500 mb-4">Get started by adding your first workshop</p>
              <Button onClick={openAddModal} className="bg-cafa-red hover:bg-cafa-red-900 text-white">
                <Plus size={16} className="mr-1" /> Add Workshop
              </Button>
            </CardContent>
          </Card>
        ) : (() => {
          // Bucket workshops into tabs (P2 admin organization)
          const today = new Date().toISOString().split('T')[0];
          const buckets = { upcoming: [], past: [], cancelled: [], archived: [] };
          workshops.forEach(w => {
            const st = (w.status || '').toLowerCase();
            if (st === 'archived') buckets.archived.push(w);
            else if (st === 'cancelled' || st === 'rescheduled') buckets.cancelled.push(w);
            else if (w.endDate && w.endDate < today) buckets.past.push(w);
            else buckets.upcoming.push(w);
          });
          buckets.upcoming.sort((a, b) => (a.startDate || a.endDate || '').localeCompare(b.startDate || b.endDate || ''));
          buckets.past.sort((a, b) => (b.endDate || '').localeCompare(a.endDate || ''));
          buckets.cancelled.sort((a, b) => (b.cancelled_at || b.endDate || '').localeCompare(a.cancelled_at || a.endDate || ''));
          buckets.archived.sort((a, b) => (b.archived_at || '').localeCompare(a.archived_at || ''));
          const tabs = [
            { key: 'upcoming',  label: 'Active / Upcoming', count: buckets.upcoming.length },
            { key: 'past',      label: 'Past',              count: buckets.past.length },
            { key: 'cancelled', label: 'Cancelled',         count: buckets.cancelled.length },
            { key: 'archived',  label: 'Archived',          count: buckets.archived.length },
          ];
          const visibleWorkshops = buckets[workshopTab] || [];
          return (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-4 border-b border-stone-200" data-testid="workshop-tabs">
              {tabs.map(t => (
                <button
                  key={t.key}
                  onClick={() => setWorkshopTab(t.key)}
                  className={`px-3 py-2 -mb-px text-sm font-medium border-b-2 transition-colors ${
                    workshopTab === t.key
                      ? 'border-cafa-red text-cafa-red'
                      : 'border-transparent text-stone-500 hover:text-stone-700'
                  }`}
                  data-testid={`workshop-tab-${t.key}`}
                  aria-current={workshopTab === t.key ? 'page' : undefined}
                >
                  {t.label}{' '}
                  <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-stone-100 text-stone-600">
                    {t.count}
                  </span>
                </button>
              ))}
            </div>
            {visibleWorkshops.length === 0 ? (
              <Card className="border-stone-200" data-testid={`workshop-tab-empty-${workshopTab}`}>
                <CardContent className="py-12 text-center">
                  <Palette size={40} className="mx-auto mb-3 text-stone-300" />
                  <p className="text-stone-500">
                    No workshops in <strong>{tabs.find(t => t.key === workshopTab)?.label}</strong>.
                  </p>
                </CardContent>
              </Card>
            ) : (
          <div className="grid gap-4">
            {visibleWorkshops.map(workshop => {
              const maxStudents = getMaxStudents(workshop.id);
              const enrolled = getEnrolled(workshop.id);
              const spotsLeft = maxStudents - enrolled;
              const isFull = spotsLeft <= 0;
              const isCancelled = workshop.status === 'cancelled';
              const isRescheduled = workshop.status === 'rescheduled';
              
              return (
                <Card key={workshop.id} className={`border-stone-200 hover:shadow-md transition-shadow ${isCancelled ? 'opacity-60 bg-red-50' : ''} ${isRescheduled ? 'opacity-70 bg-yellow-50' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      {/* Instructor Image */}
                      {workshop.instructorImage ? (
                        <img 
                          src={workshop.instructorImage} 
                          alt={workshop.instructor}
                          className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-20 h-20 rounded-lg bg-stone-200 flex items-center justify-center flex-shrink-0">
                          <User size={32} className="text-stone-400" />
                        </div>
                      )}
                      
                      {/* Workshop Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="font-semibold text-stone-900 text-lg">{workshop.title}</h3>
                            <p className="text-cafa-red font-medium">{workshop.instructor}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Status badges - show cancelled/rescheduled prominently */}
                            {isCancelled && (
                              <span className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded">
                                CANCELLED
                              </span>
                            )}
                            {isRescheduled && (
                              <span className="px-2 py-1 bg-yellow-600 text-white text-xs font-bold rounded">
                                RESCHEDULED
                              </span>
                            )}
                            {workshop.status === 'archived' && (
                              <span className="px-2 py-1 bg-purple-600 text-white text-xs font-bold rounded" data-testid={`badge-archived-${workshop.id}`}>
                                ARCHIVED
                              </span>
                            )}
                            {workshop.status === 'draft' && (
                              <span className="px-2 py-1 bg-amber-500 text-white text-xs font-bold rounded" data-testid={`badge-draft-${workshop.id}`}>
                                DRAFT
                              </span>
                            )}
                            {(() => {
                              const today = new Date().toISOString().split('T')[0];
                              const isPast = workshop.endDate && workshop.endDate < today &&
                                workshop.status !== 'archived' && workshop.status !== 'cancelled' && workshop.status !== 'rescheduled';
                              return isPast ? (
                                <span className="px-2 py-1 bg-stone-400 text-white text-xs font-bold rounded" data-testid={`badge-past-${workshop.id}`}>
                                  PAST
                                </span>
                              ) : null;
                            })()}
                            {!workshop.endDate && (
                              <span
                                className="px-1.5 py-0.5 text-xs font-semibold bg-amber-100 text-amber-700 rounded border border-amber-300"
                                title="No end date set — set one so admins can sort by past/upcoming correctly"
                                data-testid={`badge-missing-end-date-${workshop.id}`}
                              >
                                Missing End Date
                              </span>
                            )}
                            {/* UPCOMING / ACTIVE LIVE badges for non-special-status workshops */}
                            {!isCancelled && !isRescheduled && workshop.status !== 'archived' && workshop.status !== 'draft' && (() => {
                              const todayStr = new Date().toISOString().split('T')[0];
                              const startDate = workshop.date || workshop.startDate;
                              const endDate = workshop.endDate;
                              if (!startDate) return null;
                              // Don't double-badge if PAST badge is already showing
                              if (endDate && endDate < todayStr) return null;
                              if (startDate <= todayStr) {
                                return (
                                  <span className="px-2 py-1 bg-green-600 text-white text-xs font-bold rounded" data-testid={`badge-active-live-${workshop.id}`}>
                                    ACTIVE / LIVE
                                  </span>
                                );
                              }
                              return (
                                <span className="px-2 py-1 bg-blue-600 text-white text-xs font-bold rounded" data-testid={`badge-upcoming-${workshop.id}`}>
                                  UPCOMING
                                </span>
                              );
                            })()}
                            {!isCancelled && !isRescheduled && (
                              <span className={`px-2 py-1 text-xs rounded ${
                                isFull ? 'bg-red-100 text-red-800' : 
                                spotsLeft <= 3 ? 'bg-yellow-100 text-yellow-800' : 
                                'bg-green-100 text-green-800'
                              }`}>
                                {isFull ? 'FULL' : `${spotsLeft} spots left`}
                              </span>
                            )}
                            {/* Capacity Warning Badges (read-only) */}
                            {enrolled > maxStudents && (
                              <span
                                className="px-1.5 py-0.5 text-xs font-semibold bg-red-100 text-red-700 rounded border border-red-300"
                                title={`${enrolled} enrolled exceeds capacity of ${maxStudents}`}
                                data-testid="workshop-badge-over-capacity"
                              >
                                Over Capacity
                              </span>
                            )}
                            {maxStudents > 100 && (
                              <span
                                className="px-1.5 py-0.5 text-xs font-semibold bg-amber-100 text-amber-700 rounded border border-amber-300"
                                title={`Capacity is set to ${maxStudents} — this appears unusually high. Please verify the correct limit.`}
                                data-testid="workshop-badge-check-capacity"
                              >
                                Unusually High Capacity — Review
                              </span>
                            )}
                            <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">
                              {(workshop.level || '')
                                .replace(/\bIntermidate\b/gi, 'Intermediate')
                                .replace(/\bBeginners\b/g, 'Beginner')}
                            </span>
                          </div>
                        </div>
                        
                        <p className="text-sm text-stone-600 mt-2 line-clamp-2">
                          {workshop.description ? workshop.description.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim().substring(0, 200) + '…' : ''}
                        </p>
                        
                        <div className="flex flex-wrap gap-4 mt-3 text-sm text-stone-500">
                          <span className="flex items-center gap-1">
                            <Calendar size={14} /> {workshop.schedule || workshop.date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock size={14} /> {workshop.time}
                          </span>
                          <span className="flex items-center gap-1">
                            <DollarSign size={14} /> ${workshop.memberPrice} / ${workshop.nonMemberPrice}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users size={14} /> {enrolled}/{maxStudents} enrolled
                          </span>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex flex-col gap-1 flex-shrink-0">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => openAddRegistrationModal(workshop)} 
                          className="text-green-700 hover:bg-green-50"
                          disabled={isCancelled || isRescheduled || workshop.status === 'archived'}
                          aria-label={`Add student to ${workshop.title}`}
                          title="Add a student to this workshop"
                        >
                          <UserPlus size={14} className="mr-1" /> Add Student
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setShowAttendeesModal(workshop)} aria-label={`View attendees for ${workshop.title}`} title="View attendees / roster (read-only on past, cancelled, or archived)">
                          <List size={14} className="mr-1" /> Attendees
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setShareRoster(workshop)}
                          aria-label={`Share roster with instructor for ${workshop.title}`}
                          title="Share roster with the instructor — creates a private link"
                          data-testid={`share-roster-btn-${workshop.id}`}>
                          <Share2 size={14} className="mr-1" /> Share Roster
                        </Button>
                        <Button variant="outline" size="sm" className="text-cafa-red hover:bg-red-50 border-red-200" onClick={() => setEmailWorkshop(workshop)} data-testid={`email-workshop-${workshop.id}`} aria-label={`Email participants of ${workshop.title}`} title={(() => {
                          const today = new Date().toISOString().split('T')[0];
                          const isPastOrArchived = workshop.status === 'archived' || workshop.status === 'cancelled' || (workshop.endDate && workshop.endDate < today);
                          return isPastOrArchived ? 'Email former students of this workshop' : 'Email current participants';
                        })()}>
                          <Mail size={14} className="mr-1" />
                          {(() => {
                            const today = new Date().toISOString().split('T')[0];
                            const isPastOrArchived = workshop.status === 'archived' || workshop.status === 'cancelled' || (workshop.endDate && workshop.endDate < today);
                            return isPastOrArchived ? 'Email Former Students' : 'Email Participants';
                          })()}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleEdit(workshop)} aria-label={`Edit ${workshop.title}`} title="Edit workshop details">
                          <Edit2 size={14} className="mr-1" /> Edit
                        </Button>

                        {/* Archive / Unarchive (non-destructive) */}
                        {workshop.status === 'archived' ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-purple-700 hover:bg-purple-50 border-purple-200"
                            onClick={() => setUnarchiveTarget(workshop)}
                            data-testid={`unarchive-btn-${workshop.id}`}
                            aria-label={`Unarchive ${workshop.title}`}
                            title="Restore this workshop to its prior status"
                          >
                            <ArchiveRestore size={14} className="mr-1" /> Unarchive
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-stone-700 hover:bg-stone-50"
                            onClick={() => setArchiveTarget(workshop)}
                            data-testid={`archive-btn-${workshop.id}`}
                            aria-label={`Archive ${workshop.title}`}
                            title="Hide from active list — keeps roster, payments, and history"
                          >
                            <Archive size={14} className="mr-1" /> Archive
                          </Button>
                        )}

                        {/* Duplicate / Revive — creates a draft copy without registrations */}
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-cafa-blue hover:bg-cafa-blue-50 border-cafa-blue-200"
                          onClick={() => setDuplicateTarget(workshop)}
                          data-testid={`duplicate-btn-${workshop.id}`}
                          aria-label={`Duplicate ${workshop.title} as a new draft`}
                          title="Create a new workshop draft based on this one (no registrations copied)"
                        >
                          <Copy size={14} className="mr-1" /> Duplicate / Revive
                        </Button>

                        {/* Danger Zone — visually separated */}
                        <div className="mt-2 pt-2 border-t border-red-200/60">
                          <p className="text-[10px] uppercase tracking-wide text-red-600/80 font-semibold mb-1 flex items-center gap-1">
                            <AlertTriangle size={10} /> Danger zone
                          </p>
                          {workshop.status !== 'cancelled' && workshop.status !== 'rescheduled' && workshop.status !== 'archived' ? (
                            <div className="flex flex-col gap-1">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-blue-600 hover:bg-blue-50 border-blue-200" 
                                onClick={() => openRescheduleModal(workshop)}
                                aria-label={`Reschedule ${workshop.title}`}
                                title="Reschedule this workshop and notify registrants"
                              >
                                <CalendarClock size={14} className="mr-1" /> Reschedule
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-orange-600 hover:bg-orange-50 border-orange-200" 
                                onClick={() => openCancelModal(workshop)}
                                aria-label={`Cancel and refund ${workshop.title}`}
                                title="Cancel this workshop AND refund payments — destructive"
                              >
                                <Ban size={14} className="mr-1" /> Cancel & Refund
                              </Button>
                            </div>
                          ) : workshop.status === 'cancelled' ? (
                            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded text-center font-medium block">
                              CANCELLED
                            </span>
                          ) : workshop.status === 'rescheduled' ? (
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded text-center font-medium block">
                              RESCHEDULED
                            </span>
                          ) : null}
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:bg-red-50 border-red-300 mt-1 w-full"
                            onClick={() => handleDelete(workshop)}
                            aria-label={`Permanently delete ${workshop.title}`}
                            title="Permanently delete — destroys workshop record (registrations preserved on cancelled/refund flow only)"
                          >
                            <Trash2 size={14} className="mr-1" /> Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
            )}
          </>
          );
        })()}
      </div>

      {/* Edit Modal */}
      {editingWorkshop && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget) setEditingWorkshop(null); }}
        >
          <div 
            className="bg-white rounded-lg max-w-3xl w-full p-6 my-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-stone-900">Edit Workshop</h2>
              <Button variant="ghost" size="sm" onClick={() => setEditingWorkshop(null)}>
                <X size={18} />
              </Button>
            </div>
            
            {workshopFormContent(true)}
            
            <div className="flex items-center gap-4 mt-6 pt-4 border-t border-stone-200">
              <Button onClick={handleSave} disabled={saving} className="flex-1 bg-cafa-red hover:bg-cafa-red-900 text-white">
                <Save size={16} className="mr-1" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button variant="outline" onClick={() => setEditingWorkshop(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget) setShowAddModal(false); }}
        >
          <div 
            className="bg-white rounded-lg max-w-3xl w-full p-6 my-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-stone-900">Add New Workshop</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowAddModal(false)}>
                <X size={18} />
              </Button>
            </div>
            
            {workshopFormContent(false)}
            
            <div className="flex items-center gap-4 mt-6 pt-4 border-t border-stone-200">
              <Button onClick={handleAdd} disabled={saving} className="flex-1 bg-cafa-red hover:bg-cafa-red-900 text-white">
                <Plus size={16} className="mr-1" />
                {saving ? 'Creating...' : 'Create Workshop'}
              </Button>
              <Button variant="outline" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Registration Modal */}
      {showAddRegistrationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-stone-900">Add Registration</h2>
                <p className="text-sm text-stone-500">{showAddRegistrationModal.title}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowAddRegistrationModal(null)}>
                <X size={18} />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Full Name <span className="text-red-500">*</span></Label>
                  <Input 
                    value={registrationForm.payer_name}
                    onChange={(e) => setRegistrationForm({...registrationForm, payer_name: e.target.value})}
                    className="mt-1"
                    placeholder="John Smith"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Email <span className="text-red-500">*</span></Label>
                  <Input 
                    type="email"
                    value={registrationForm.payer_email}
                    onChange={(e) => setRegistrationForm({...registrationForm, payer_email: e.target.value})}
                    className="mt-1"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input 
                    value={registrationForm.payer_phone}
                    onChange={(e) => setRegistrationForm({...registrationForm, payer_phone: e.target.value})}
                    className="mt-1"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <Label>Amount Paid ($)</Label>
                  <Input 
                    type="number"
                    value={registrationForm.amount_paid}
                    onChange={(e) => setRegistrationForm({...registrationForm, amount_paid: parseFloat(e.target.value) || 0})}
                    className="mt-1"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Payment Method</Label>
                  <select 
                    value={registrationForm.payment_method}
                    onChange={(e) => setRegistrationForm({...registrationForm, payment_method: e.target.value})}
                    className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-md"
                  >
                    <option value="cash">Cash</option>
                    <option value="check">Check</option>
                    <option value="card">Credit/Debit Card</option>
                    <option value="paypal">PayPal (offline)</option>
                    <option value="venmo">Venmo</option>
                    <option value="zelle">Zelle</option>
                    <option value="scholarship">Scholarship/Comp</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <Label>Notes</Label>
                  <Textarea 
                    value={registrationForm.notes}
                    onChange={(e) => setRegistrationForm({...registrationForm, notes: e.target.value})}
                    className="mt-1"
                    rows={2}
                    placeholder="Any additional notes about this registration..."
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-4 pt-4 border-t border-stone-200">
                <Button onClick={handleAddRegistration} disabled={saving} className="flex-1 bg-green-700 hover:bg-green-800 text-white">
                  <UserPlus size={16} className="mr-1" />
                  {saving ? 'Adding...' : 'Add Registration'}
                </Button>
                <Button variant="outline" onClick={() => setShowAddRegistrationModal(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Attendees Modal */}
      {showAttendeesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-stone-900">Roster / Attendance Records</h2>
                <p className="text-sm text-stone-500">{showAttendeesModal.title}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowAttendeesModal(null)}>
                <X size={18} />
              </Button>
            </div>

            {/* Roster vs Card Count Mismatch Warning (read-only) */}
            {(() => {
              const cardEnrolled = getEnrolled(showAttendeesModal.id);
              const rosterActive = getAttendees(showAttendeesModal.id).filter(a => a.status !== 'cancelled').length;
              if (cardEnrolled === rosterActive) return null;
              return (
                <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-2" data-testid="workshop-roster-mismatch-warning">
                  <span className="text-amber-700 leading-none">⚠️</span>
                  <div className="text-sm text-amber-900">
                    <div className="font-semibold">Enrollment count mismatch</div>
                    <div className="text-xs mt-1">
                      Card shows <strong>{cardEnrolled}</strong> enrolled, but the active roster contains <strong>{rosterActive}</strong> non-cancelled attendees.
                      Informational only — no records have been changed.
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Enrollment Stats Bar */}
            {(() => {
              const allAttendees = getAttendees(showAttendeesModal.id);
              const activeCount = getEnrolled(showAttendeesModal.id);
              const cancelledRefunded = allAttendees.filter(a => a.status === 'cancelled' || a.refund_status === 'refunded').length;
              const capacity = getMaxStudents(showAttendeesModal.id);
              return (
                <div className="flex flex-wrap gap-4 mb-3 p-3 bg-stone-50 border border-stone-200 rounded-lg text-sm" data-testid="attendees-stats-bar">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block flex-shrink-0"></span>
                    <span className="text-stone-600">Active Enrolled: <strong className="text-green-700">{activeCount}</strong></span>
                  </div>
                  {cancelledRefunded > 0 && (
                    <div className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-red-400 inline-block flex-shrink-0"></span>
                      <span className="text-stone-600">Cancelled / Refunded: <strong className="text-red-600">{cancelledRefunded}</strong></span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-stone-400 inline-block flex-shrink-0"></span>
                    <span className="text-stone-600">Capacity: <strong className="text-stone-700">{capacity}</strong></span>
                  </div>
                  <span className="text-xs text-stone-400 italic ml-auto self-center">All registration records shown below</span>
                </div>
              );
            })()}
            
            <div className="flex-1 overflow-y-auto">
              {getAttendees(showAttendeesModal.id).length === 0 ? (
                <div className="text-center py-8 text-stone-500">
                  <Users size={32} className="mx-auto mb-2 text-stone-300" />
                  <p>No attendees registered yet</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-stone-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-stone-500 uppercase">#</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-stone-500 uppercase">Name</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-stone-500 uppercase">Email</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-stone-500 uppercase">Phone</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-stone-500 uppercase">Amount</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-stone-500 uppercase">Status</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-stone-500 uppercase">Pricing</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-stone-500 uppercase">Date</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-stone-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-200">
                    {getAttendees(showAttendeesModal.id).map((attendee, idx) => {
                      const isCancelledRow = attendee.status === 'cancelled' || attendee.refund_status === 'refunded';
                      return (
                      <tr key={idx} className={`hover:bg-stone-50 ${isCancelledRow ? 'opacity-60 bg-red-50/40' : ''}`}>
                        <td className="px-3 py-2 text-sm text-stone-500">{idx + 1}</td>
                        <td className={`px-3 py-2 font-medium ${isCancelledRow ? 'text-stone-500 line-through' : 'text-stone-900'}`}>{attendee.name || 'N/A'}</td>
                        <td className="px-3 py-2 text-sm text-stone-600">{attendee.email || 'N/A'}</td>
                        <td className="px-3 py-2 text-sm text-stone-600" data-testid={`attendee-phone-${idx}`}>
                          {attendee.phone ? (
                            <a href={`tel:${attendee.phone}`} className="text-amber-700 hover:underline">{attendee.phone}</a>
                          ) : 'N/A'}
                        </td>
                        <td className="px-3 py-2 text-sm text-stone-600">${attendee.amount?.toFixed(2) || '0.00'}</td>
                        <td className="px-3 py-2 text-sm">
                          {attendee.refund_status === 'refunded' ? (
                            <span className="px-1.5 py-0.5 text-xs bg-orange-100 text-orange-700 rounded font-medium">Refunded</span>
                          ) : attendee.status === 'cancelled' ? (
                            <span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded font-medium">Cancelled</span>
                          ) : (
                            <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded font-medium">Active</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-sm">
                          {attendee.price_tier === 'member' ? (
                            <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded font-medium" title={attendee.member_verified_at ? `Verified at ${new Date(attendee.member_verified_at).toLocaleDateString()}` : 'Member price'}>
                              Member Price — Verified
                            </span>
                          ) : attendee.price_tier === 'non_member' ? (
                            <span className="px-1.5 py-0.5 text-xs bg-stone-100 text-stone-600 rounded font-medium">Non-Member Price</span>
                          ) : (
                            <span className="px-1.5 py-0.5 text-xs bg-stone-100 text-stone-400 rounded font-medium">Legacy / Unknown</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-sm text-stone-500">
                          {attendee.date ? new Date(attendee.date).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-3 py-2">
                          {attendee.status !== 'cancelled' && attendee.refund_status !== 'refunded' && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-red-600 hover:bg-red-50 border-red-300 text-xs"
                              onClick={() => openIndividualCancelModal(showAttendeesModal, attendee)}
                              title="Cancel this individual registration — triggers refund policy check"
                            >
                              <UserX size={12} className="mr-1" /> Cancel
                            </Button>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            
            <div className="flex items-center justify-between pt-4 border-t border-stone-200 mt-4">
              <div className="text-sm text-stone-500">
                <span>Active enrolled: <strong className="text-green-700">{getEnrolled(showAttendeesModal.id)}</strong> / {getMaxStudents(showAttendeesModal.id)} capacity</span>
                {getAttendees(showAttendeesModal.id).length !== getEnrolled(showAttendeesModal.id) && (
                  <span className="text-stone-400 ml-2">({getAttendees(showAttendeesModal.id).length} total records incl. cancelled)</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-stone-400 uppercase tracking-wide font-medium">Manage:</span>
                <Button variant="outline" size="sm" onClick={() => openAddRegistrationModal(showAttendeesModal)} title="Add a new student registration to this workshop">
                  <UserPlus size={14} className="mr-1" /> Add Student
                </Button>
                <Button variant="outline" onClick={() => setShowAttendeesModal(null)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Data Sync Modal */}
      <Dialog open={showSyncModal} onOpenChange={setShowSyncModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database size={20} /> Sync Workshop Data to Production
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">How Data Sync Works</h4>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>Workshop data is exported from this preview environment</li>
                <li>Enter your production site URL and admin token</li>
                <li>Click &quot;Sync to Production&quot; to push the data</li>
              </ol>
            </div>

            {syncData && (
              <div className="bg-stone-50 rounded-lg p-4 border border-stone-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-stone-900">Exported Data</h4>
                  <Button variant="outline" size="sm" onClick={copyExportData}>
                    <Download size={14} className="mr-1" /> Copy JSON
                  </Button>
                </div>
                <p className="text-sm text-stone-600 mb-2">{syncData.count} workshops ready to sync:</p>
                <ul className="text-sm text-stone-600 max-h-32 overflow-y-auto space-y-1">
                  {syncData.workshops?.map((w, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded bg-cafa-red text-white text-xs flex items-center justify-center">{w.id}</span>
                      {w.title}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="space-y-3 border-t border-stone-200 pt-4">
              <h4 className="font-medium text-stone-900">Production Credentials</h4>
              
              <div>
                <Label>Production Site URL</Label>
                <Input
                  value={productionUrl}
                  onChange={(e) => setProductionUrl(e.target.value)}
                  placeholder="https://your-production-site.com"
                  className="mt-1"
                />
                <p className="text-xs text-stone-500 mt-1">The URL of your deployed production site</p>
              </div>
              
              <div>
                <Label>Admin Token</Label>
                <Input
                  type="password"
                  value={productionToken}
                  onChange={(e) => setProductionToken(e.target.value)}
                  placeholder="Paste your admin JWT token here"
                  className="mt-1"
                />
                <p className="text-xs text-stone-500 mt-1">
                  Get this by logging into production admin, then run in browser console: <code className="bg-stone-100 px-1 rounded">localStorage.getItem(&apos;token&apos;)</code>
                </p>
              </div>
            </div>

            <div className="flex gap-2 pt-4 border-t border-stone-200">
              <Button 
                onClick={handleSyncToProduction}
                disabled={syncing || !syncData || !productionUrl || !productionToken}
                className="flex-1 bg-cafa-blue hover:bg-cafa-blue-900 text-white"
              >
                {syncing ? (
                  <><RefreshCw size={16} className="mr-1 animate-spin" /> Syncing...</>
                ) : (
                  <><CloudUpload size={16} className="mr-1" /> Sync to Production</>
                )}
              </Button>
              <Button variant="outline" onClick={() => setShowSyncModal(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Workshop Cancellation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 my-8 max-h-[90vh] overflow-y-auto">
            {!cancelResult ? (
              <>
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                      <AlertTriangle size={20} className="text-orange-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-stone-900">Cancel Workshop & Refund All</h2>
                      <p className="text-sm text-stone-500">{showCancelModal.title}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={closeCancelModal}>
                    <X size={18} />
                  </Button>
                </div>

                {/* Warning Banner */}
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-orange-800 mb-2">⚠️ This action will:</h4>
                  <ul className="text-sm text-orange-700 space-y-1 list-disc list-inside">
                    <li>Mark the workshop as <strong>cancelled</strong> (prevents new registrations)</li>
                    <li>Process <strong>full refunds</strong> via PayPal for all registered students</li>
                    <li>Send <strong>cancellation notification emails</strong> to all affected students</li>
                  </ul>
                  <p className="text-sm text-orange-800 mt-2 font-medium">This action cannot be undone.</p>
                </div>

                {/* Preview Data */}
                {!cancelPreview ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw size={24} className="animate-spin text-stone-400" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Summary Stats */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-stone-100 rounded-lg p-4 text-center">
                        <p className="text-3xl font-bold text-stone-900">{cancelPreview.registration_count}</p>
                        <p className="text-sm text-stone-600">Registered Students</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4 text-center">
                        <p className="text-3xl font-bold text-green-700">${cancelPreview.total_refund_amount.toFixed(2)}</p>
                        <p className="text-sm text-green-600">Total Refund Amount</p>
                      </div>
                    </div>

                    {/* Registrant List */}
                    {cancelPreview.registrants && cancelPreview.registrants.length > 0 && (
                      <div className="border border-stone-200 rounded-lg overflow-hidden">
                        <div className="bg-stone-50 px-4 py-2 border-b border-stone-200">
                          <h4 className="font-medium text-stone-700 text-sm">Students to be refunded:</h4>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-stone-50 sticky top-0">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-stone-500 uppercase">Name</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-stone-500 uppercase">Email</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-stone-500 uppercase">Amount</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                              {cancelPreview.registrants.map((reg, idx) => (
                                <tr key={idx}>
                                  <td className="px-4 py-2 text-stone-900">{reg.name}</td>
                                  <td className="px-4 py-2 text-stone-600">{reg.email}</td>
                                  <td className="px-4 py-2 text-right text-stone-900">${reg.amount.toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Cancellation Reason */}
                    <div>
                      <Label>Cancellation Reason (Optional)</Label>
                      <Textarea
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        className="mt-1"
                        rows={2}
                        placeholder="e.g., Instructor illness, low enrollment, venue unavailable..."
                      />
                      <p className="text-xs text-stone-500 mt-1">This will be included in the email sent to students.</p>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-4 mt-6 pt-4 border-t border-stone-200">
                  <Button 
                    onClick={handleCancelWorkshop} 
                    disabled={cancelling || !cancelPreview}
                    className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    {cancelling ? (
                      <><RefreshCw size={16} className="mr-2 animate-spin" /> Processing...</>
                    ) : (
                      <><Ban size={16} className="mr-2" /> Cancel Workshop & Refund All</>
                    )}
                  </Button>
                  <Button variant="outline" onClick={closeCancelModal} disabled={cancelling}>
                    Keep Workshop
                  </Button>
                </div>
              </>
            ) : (
              /* Results View */
              <>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <Ban size={20} className="text-green-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-stone-900">Workshop Cancelled</h2>
                      <p className="text-sm text-stone-500">{cancelResult.workshop_name}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={closeCancelModal}>
                    <X size={18} />
                  </Button>
                </div>

                {/* Success Summary */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-green-800 mb-2">✓ Cancellation Complete</h4>
                  <p className="text-sm text-green-700">
                    The workshop has been cancelled and all registered students have been processed.
                  </p>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="bg-stone-100 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-stone-900">{cancelResult.total_registrations}</p>
                    <p className="text-xs text-stone-600">Total Registrations</p>
                  </div>
                  <div className="bg-green-100 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-700">{cancelResult.successful_refunds}</p>
                    <p className="text-xs text-green-600">Refunds Processed</p>
                  </div>
                  <div className={`${cancelResult.failed_refunds > 0 ? 'bg-red-100' : 'bg-stone-100'} rounded-lg p-3 text-center`}>
                    <p className={`text-2xl font-bold ${cancelResult.failed_refunds > 0 ? 'text-red-700' : 'text-stone-400'}`}>
                      {cancelResult.failed_refunds}
                    </p>
                    <p className={`text-xs ${cancelResult.failed_refunds > 0 ? 'text-red-600' : 'text-stone-500'}`}>Failed Refunds</p>
                  </div>
                  <div className="bg-blue-100 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-blue-700">{cancelResult.emails_sent}</p>
                    <p className="text-xs text-blue-600">Emails Sent</p>
                  </div>
                </div>

                {/* Total Refunded */}
                <div className="bg-green-50 rounded-lg p-4 mb-4 text-center">
                  <p className="text-sm text-green-600 mb-1">Total Amount Refunded</p>
                  <p className="text-3xl font-bold text-green-700">${cancelResult.total_refund_amount.toFixed(2)}</p>
                </div>

                {/* Failed Refunds Warning */}
                {cancelResult.failed_refunds > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <h4 className="font-medium text-red-800 mb-2">⚠️ Some Refunds Failed</h4>
                    <p className="text-sm text-red-700 mb-2">
                      {cancelResult.failed_refunds} refund(s) could not be processed automatically. 
                      These will need manual attention.
                    </p>
                    <ul className="text-sm text-red-700 space-y-1">
                      {cancelResult.refund_details
                        .filter(r => r.refund_status === 'failed')
                        .map((r, idx) => (
                          <li key={idx}>• {r.student_name} ({r.student_email}): {r.error_message}</li>
                        ))}
                    </ul>
                  </div>
                )}

                {/* Close Button */}
                <div className="flex justify-end pt-4 border-t border-stone-200">
                  <Button onClick={closeCancelModal} className="bg-stone-800 hover:bg-stone-900 text-white">
                    Done
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Reschedule Workshop Modal */}
      {showRescheduleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 my-8 max-h-[90vh] overflow-y-auto">
            {!rescheduleResult ? (
              <>
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <CalendarClock size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-stone-900">Reschedule Workshop</h2>
                      <p className="text-sm text-stone-500">{showRescheduleModal.title}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={closeRescheduleModal}>
                    <X size={18} />
                  </Button>
                </div>

                {/* Info Banner */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-blue-800 mb-2">📅 How Rescheduling Works</h4>
                  <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                    <li>Creates a new workshop with the new dates</li>
                    <li>Automatically <strong>moves all registrations</strong> to the new workshop</li>
                    <li><strong>No refunds</strong> are issued (standard policy)</li>
                    <li>All registrants receive email notification with option to request cancellation</li>
                  </ul>
                </div>

                {/* Preview Data */}
                {!reschedulePreview ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw size={24} className="animate-spin text-stone-400" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Current Info */}
                    <div className="bg-stone-100 rounded-lg p-4">
                      <p className="text-sm text-stone-500 mb-1">Current Schedule</p>
                      <p className="font-medium text-stone-800">{reschedulePreview.current_date || 'Not set'}</p>
                      <p className="text-sm text-stone-600 mt-2">
                        <strong>{reschedulePreview.registration_count}</strong> students currently registered
                      </p>
                    </div>

                    {/* New Dates */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>New Start Date <span className="text-red-500">*</span></Label>
                        <Input
                          type="date"
                          value={rescheduleData.newStartDate}
                          onChange={(e) => setRescheduleData({...rescheduleData, newStartDate: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>New End Date</Label>
                        <Input
                          type="date"
                          value={rescheduleData.newEndDate}
                          onChange={(e) => setRescheduleData({...rescheduleData, newEndDate: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div>
                      <Label>New Schedule Description</Label>
                      <Input
                        value={rescheduleData.newSchedule}
                        onChange={(e) => setRescheduleData({...rescheduleData, newSchedule: e.target.value})}
                        className="mt-1"
                        placeholder="e.g., Mon-Fri, 9:00 AM - 4:00 PM"
                      />
                    </div>

                    <div>
                      <Label>Message to Registrants (Optional)</Label>
                      <Textarea
                        value={rescheduleData.message}
                        onChange={(e) => setRescheduleData({...rescheduleData, message: e.target.value})}
                        className="mt-1"
                        rows={3}
                        placeholder="Explain why the workshop is being rescheduled..."
                      />
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-4 mt-6 pt-4 border-t border-stone-200">
                  <Button 
                    onClick={handleRescheduleWorkshop} 
                    disabled={rescheduling || !reschedulePreview || !rescheduleData.newStartDate}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {rescheduling ? (
                      <><RefreshCw size={16} className="mr-2 animate-spin" /> Processing...</>
                    ) : (
                      <><CalendarClock size={16} className="mr-2" /> Reschedule Workshop</>
                    )}
                  </Button>
                  <Button variant="outline" onClick={closeRescheduleModal} disabled={rescheduling}>
                    Cancel
                  </Button>
                </div>
              </>
            ) : (
              /* Results View */
              <>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <CalendarClock size={20} className="text-green-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-stone-900">Workshop Rescheduled</h2>
                      <p className="text-sm text-stone-500">{showRescheduleModal.title}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={closeRescheduleModal}>
                    <X size={18} />
                  </Button>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-green-800 mb-2">✓ Reschedule Complete</h4>
                  <p className="text-sm text-green-700">
                    The workshop has been rescheduled and all registrations have been moved.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-stone-100 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-stone-900">{rescheduleResult.registrations_moved}</p>
                    <p className="text-xs text-stone-600">Registrations Moved</p>
                  </div>
                  <div className="bg-blue-100 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-blue-700">{rescheduleResult.emails_sent}</p>
                    <p className="text-xs text-blue-600">Notifications Sent</p>
                  </div>
                </div>

                <div className="bg-stone-50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-stone-600">New Workshop ID: <strong>#{rescheduleResult.new_workshop_id}</strong></p>
                  <p className="text-sm text-stone-600">New Date: <strong>{rescheduleResult.new_date}</strong></p>
                </div>

                <div className="flex justify-end pt-4 border-t border-stone-200">
                  <Button onClick={closeRescheduleModal} className="bg-stone-800 hover:bg-stone-900 text-white">
                    Done
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Individual Cancellation Modal */}
      {showIndividualCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-xl w-full p-6 my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <UserX size={20} className="text-red-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-stone-900">Cancel Registration</h2>
                  <p className="text-sm text-stone-500">{showIndividualCancelModal.registration?.name}</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowIndividualCancelModal(null)}>
                <X size={18} />
              </Button>
            </div>

            {!individualCancelEligibility ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw size={24} className="animate-spin text-stone-400" />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Policy Info */}
                <div className="bg-stone-100 rounded-lg p-4">
                  <h4 className="font-medium text-stone-800 mb-2">Cancellation Policy</h4>
                  <ul className="text-sm text-stone-600 space-y-1 list-disc list-inside">
                    <li>More than 45 days before: Refund minus $100 fee</li>
                    <li>45 days or fewer: No refund</li>
                  </ul>
                </div>

                {/* Eligibility Result */}
                <div className={`rounded-lg p-4 ${
                  individualCancelEligibility.policy_applied === 'no_refund' 
                    ? 'bg-red-50 border border-red-200' 
                    : 'bg-green-50 border border-green-200'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-stone-700">Days until workshop:</span>
                    <span className="font-bold text-stone-900">{individualCancelEligibility.days_before_start}</span>
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-stone-700">Amount paid:</span>
                    <span className="font-bold text-stone-900">${individualCancelEligibility.amount_paid.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-stone-700">Cancellation fee:</span>
                    <span className="font-bold text-stone-900">${individualCancelEligibility.cancellation_fee.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-stone-300 mt-2">
                    <span className="text-sm font-medium text-stone-700">Refund amount:</span>
                    <span className={`font-bold text-lg ${
                      individualCancelEligibility.refund_amount > 0 ? 'text-green-700' : 'text-red-700'
                    }`}>
                      ${individualCancelEligibility.refund_amount.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-stone-500 mt-2 italic">{individualCancelEligibility.policy_explanation}</p>
                </div>

                {/* Request Method */}
                <div>
                  <Label>Request Method <span className="text-red-500">*</span></Label>
                  <select
                    value={individualCancelMethod}
                    onChange={(e) => setIndividualCancelMethod(e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-md"
                  >
                    <option value="email">Email Request</option>
                    <option value="website_form">Website Form</option>
                  </select>
                  <p className="text-xs text-stone-500 mt-1">Phone, text, and social media requests not accepted per policy.</p>
                </div>

                {/* Reason */}
                <div>
                  <Label>Cancellation Reason <span className="text-red-500">*</span></Label>
                  <Textarea
                    value={individualCancelReason}
                    onChange={(e) => setIndividualCancelReason(e.target.value)}
                    className="mt-1"
                    rows={2}
                    placeholder="Why is the student cancelling?"
                  />
                </div>

                {/* Actions */}
                <div className="flex items-center gap-4 pt-4 border-t border-stone-200">
                  <Button 
                    onClick={handleSubmitIndividualCancellation} 
                    disabled={processingIndividualCancel || !individualCancelReason}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  >
                    {processingIndividualCancel ? (
                      <><RefreshCw size={16} className="mr-2 animate-spin" /> Processing...</>
                    ) : (
                      <><UserX size={16} className="mr-2" /> Process Cancellation</>
                    )}
                  </Button>
                  <Button variant="outline" onClick={() => setShowIndividualCancelModal(null)} disabled={processingIndividualCancel}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
      {/* Email Participants Modal */}
      <EmailParticipantsModal
        open={!!emailWorkshop}
        onClose={() => setEmailWorkshop(null)}
        sourceType="workshop"
        sourceId={emailWorkshop ? String(emailWorkshop.id) : ''}
        sourceName={emailWorkshop?.title || ''}
        instructor={emailWorkshop?.instructor || ''}
        schedule={emailWorkshop?.startDate ? `${emailWorkshop.startDate}${emailWorkshop.endDate ? ' - ' + emailWorkshop.endDate : ''}` : ''}
        modalTitle={(() => {
          const today = new Date().toISOString().split('T')[0];
          const isPastOrArchived = emailWorkshop?.status === 'archived' || emailWorkshop?.status === 'cancelled' || (emailWorkshop?.endDate && emailWorkshop.endDate < today);
          return isPastOrArchived ? 'Email Former Students' : 'Email Participants';
        })()}
      />

      {shareRoster && (
        <ShareRosterModal
          itemType="workshop"
          item={shareRoster}
          token={token}
          onClose={() => setShareRoster(null)}
        />
      )}

      {/* Archive Confirmation */}
      <AlertDialog open={!!archiveTarget} onOpenChange={(open) => { if (!open) setArchiveTarget(null); }}>
        <AlertDialogContent data-testid="archive-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-stone-800">
              <Archive className="text-stone-700" size={20} />
              Archive this workshop?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base text-stone-700 space-y-2">
              <span className="block">
                <strong>{archiveTarget?.title}</strong>
              </span>
              <span className="block text-sm bg-stone-50 border border-stone-200 rounded p-2">
                Archiving hides this workshop from the active list but <strong>keeps all registrations, payments, attendee history, and instructor info</strong>. You can unarchive at any time. Nothing is deleted.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="archive-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-stone-800 hover:bg-stone-900 text-white"
              onClick={() => archiveTarget && performArchive(archiveTarget)}
              disabled={actionLoading}
              data-testid="archive-confirm-btn"
            >
              <Archive size={14} className="mr-2" />
              {actionLoading ? 'Archiving...' : 'Archive Workshop'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unarchive Confirmation */}
      <AlertDialog open={!!unarchiveTarget} onOpenChange={(open) => { if (!open) setUnarchiveTarget(null); }}>
        <AlertDialogContent data-testid="unarchive-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-purple-800">
              <ArchiveRestore className="text-purple-700" size={20} />
              Unarchive this workshop?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base text-stone-700 space-y-2">
              <span className="block">
                <strong>{unarchiveTarget?.title}</strong>
              </span>
              <span className="block text-sm bg-purple-50 border border-purple-200 rounded p-2 text-purple-900">
                The workshop will return to its <strong>{unarchiveTarget?.prior_status || 'scheduled'}</strong> status and reappear in the appropriate tab. Registrations and history are unchanged.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="unarchive-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-purple-700 hover:bg-purple-800 text-white"
              onClick={() => unarchiveTarget && performUnarchive(unarchiveTarget)}
              disabled={actionLoading}
              data-testid="unarchive-confirm-btn"
            >
              <ArchiveRestore size={14} className="mr-2" />
              {actionLoading ? 'Unarchiving...' : 'Unarchive Workshop'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Duplicate / Revive Confirmation */}
      <AlertDialog open={!!duplicateTarget} onOpenChange={(open) => { if (!open) setDuplicateTarget(null); }}>
        <AlertDialogContent data-testid="duplicate-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-cafa-blue">
              <Copy size={20} />
              Duplicate this workshop as a new draft?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base text-stone-700 space-y-2">
              <span className="block">
                <strong>{duplicateTarget?.title}</strong>
              </span>
              <span className="block text-sm bg-blue-50 border border-blue-200 rounded p-2 text-blue-900">
                A new <strong>draft</strong> workshop will be created with the title, instructor, description, price, capacity, and images copied. <strong>Registrations, payments, attendee history, dates, and cancellation/archive flags are NOT copied.</strong> Set new start/end dates before publishing.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="duplicate-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-cafa-blue hover:bg-cafa-blue-900 text-white"
              onClick={() => duplicateTarget && performDuplicate(duplicateTarget)}
              disabled={actionLoading}
              data-testid="duplicate-confirm-btn"
            >
              <Copy size={14} className="mr-2" />
              {actionLoading ? 'Creating draft...' : 'Create Draft Copy'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </AdminLayout>
  );
};

export default AdminWorkshops;
